/**
 * Schemas for the enrichment staging layer (phone find): status enums, DB row
 * shapes for `queryRawParsed`, and the wire contracts. Types are `z.infer` only.
 */
import { z } from 'zod';

export const EnrichmentRequestStatusSchema = z.enum([
  'pending',
  'running',
  'found',
  'not_found',
  'failed',
]);

/** Statuses of a request that is still being worked. Derived so a renamed status breaks the build. */
export const OPEN_REQUEST_STATUSES = EnrichmentRequestStatusSchema.options.filter(
  (s): s is 'pending' | 'running' => s === 'pending' || s === 'running',
);

export const PHONE_KIND = 'phone';
export const PHONE_FIELD = 'best_phone';
/** SSE invalidate reason that tells the client to refetch phone-request states. */
export const PHONE_REQUEST_INVALIDATE_REASON = 'phone_request';
/** The server caps a start body (and a state read) at this many contacts. */
export const MAX_PHONE_REQUEST_CONTACTS = 200;
/** Per-request time limit. */
export const PHONE_REQUEST_DEADLINE_MS = 180_000;
/** How long a staged number overlays the mirror (the SQL interval and the store TTL both derive from it). */
export const STAGED_PHONE_TTL_HOURS = 48;

export const StagedFieldStatusSchema = z.enum([
  'pending',
  'applied',
  'confirmed',
  'superseded',
  'failed',
  'wrong',
]);

export const EnrichmentRequestSourceSchema = z.enum(['bulk_dial', 'row_button', 'contact_card']);

const nullableString = z.string().nullable();

export const EnrichmentRequestRowSchema = z.object({
  id: z.string(),
  kind: z.string(),
  status: EnrichmentRequestStatusSchema,
  step: z.number().int().nullable(),
  source: EnrichmentRequestSourceSchema,
  requester_user_id: z.number().int(),
  requester_slack_user_id: z.string(),
  contact_id: nullableString,
  full_name: nullableString,
  email: nullableString,
  linkedin_url: nullableString,
  account_name: nullableString,
  account_domain: nullableString,
  step1_execution_id: nullableString,
  step2_execution_id: nullableString,
  failure_message: nullableString,
  created_at: z.coerce.date(),
  started_at: z.coerce.date().nullable(),
  finished_at: z.coerce.date().nullable(),
  deadline_at: z.coerce.date(),
  updated_at: z.coerce.date(),
});

/** Row shape for the phone-finder contact hydration query. */
export const HydratedContactRowSchema = z.object({
  id: z.string(),
  name: nullableString,
  email: nullableString,
  linkedin: nullableString,
  best_phone: nullableString,
  account_name: nullableString,
  account_domain: nullableString,
});

export const PhoneRequestStateSchema = z.object({
  contact_id: z.string(),
  state: z.enum(['finding', 'ready', 'not_found', 'failed']),
  value: nullableString,
  source: nullableString,
  low_confidence: z.boolean(),
  message: nullableString,
  request_id: nullableString,
  updated_at: z.string(),
});

export const PhoneRequestStatesResponseSchema = z.array(PhoneRequestStateSchema);

export const StartPhoneRequestsBodySchema = z.object({
  contact_ids: z.array(z.string().min(1)).min(1).max(MAX_PHONE_REQUEST_CONTACTS),
  source: EnrichmentRequestSourceSchema,
});

export const StartPhoneRequestsResponseSchema = z.object({
  started: z.array(z.string()),
  joined: z.array(z.string()),
  alreadyHasNumber: z.array(z.string()),
  failed: z.array(z.string()),
});
export type StartPhoneRequestsResponse = z.infer<typeof StartPhoneRequestsResponseSchema>;

export const MarkPhoneWrongBodySchema = z.object({ value: z.string().min(1) });

export type EnrichmentRequestStatus = z.infer<typeof EnrichmentRequestStatusSchema>;
export type StagedFieldStatus = z.infer<typeof StagedFieldStatusSchema>;
export type EnrichmentRequestSource = z.infer<typeof EnrichmentRequestSourceSchema>;
export type EnrichmentRequestRow = z.infer<typeof EnrichmentRequestRowSchema>;
export type HydratedContactRow = z.infer<typeof HydratedContactRowSchema>;
export type PhoneRequestState = z.infer<typeof PhoneRequestStateSchema>;
export type PhoneRequestStatesResponse = z.infer<typeof PhoneRequestStatesResponseSchema>;
export type StartPhoneRequestsBody = z.infer<typeof StartPhoneRequestsBodySchema>;
export type MarkPhoneWrongBody = z.infer<typeof MarkPhoneWrongBodySchema>;
